import React, { useEffect, useState } from 'react';
import {
  View,
  Text,
  Modal,
  TouchableOpacity,
  ScrollView,
  ActivityIndicator,
  StyleSheet,
  Platform,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { colors, spacing, typography } from '../theme';
import { apiService, CalendarSharedWithEntry } from '../services/api';
import { useBusinessData } from '../hooks/useBusinessData';
import { useThemedStyles } from '../hooks/useThemedStyles';
import { withAlpha } from '../theme';

const MOBILE_HEADER_OFFSET = 72;

interface CalendarSharingModalProps {
  visible: boolean;
  onClose: () => void;
  calendarId: string;
  calendarName: string;
  currentSharedWith: CalendarSharedWithEntry[];
  onUpdated?: (sharedWith: CalendarSharedWithEntry[]) => void;
  onNotify: (type: 'success' | 'error', title: string, message: string) => void;
}

/**
 * Assigns a business calendar to users/departments — this IS calendar
 * "assignment": Calendar.sharedWith is the single mechanism for both
 * assignment and cross-business sharing (see calendar.entity.ts's doc
 * comment), so this writes through updateCalendarSharedWith rather than the
 * generic Assignment module. Only meaningful for business (not personal)
 * calendars — callers should only render this when calendar.businessId is set.
 */
export const CalendarSharingModal: React.FC<CalendarSharingModalProps> = ({
  visible,
  onClose,
  calendarId,
  calendarName,
  currentSharedWith,
  onUpdated,
  onNotify,
}) => {
  const styles = useThemedStyles(getStyles);
  const { users, departments, isLoadingUsers, isLoadingDepartments } = useBusinessData();
  const [sharedWith, setSharedWith] = useState<CalendarSharedWithEntry[]>(currentSharedWith);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (visible) setSharedWith(currentSharedWith);
  }, [visible, currentSharedWith]);

  const isUserShared = (userId: string) => sharedWith.some((e) => e.userId === userId);
  const isDepartmentShared = (departmentId: string) => sharedWith.some((e) => e.departmentId === departmentId);

  const toggleUser = (userId: string) => {
    setSharedWith((prev) =>
      prev.some((e) => e.userId === userId)
        ? prev.filter((e) => e.userId !== userId)
        : [...prev, { userId, permission: 'edit' }]
    );
  };

  const toggleDepartment = (departmentId: string) => {
    setSharedWith((prev) =>
      prev.some((e) => e.departmentId === departmentId)
        ? prev.filter((e) => e.departmentId !== departmentId)
        : [...prev, { departmentId, permission: 'edit' }]
    );
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await apiService.updateCalendarSharedWith(calendarId, sharedWith);
      if (res.statusCode === 1000) {
        onNotify('success', 'Calendar updated', `Sharing settings saved for "${calendarName}".`);
        onUpdated?.(sharedWith);
        onClose();
      } else {
        onNotify('error', 'Could not update sharing', res.errors?.[0]?.message || 'Please try again.');
      }
    } catch (err: any) {
      console.error('[CalendarSharingModal] Failed to update sharing:', err);
      onNotify('error', 'Could not update sharing', err?.message || 'Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal visible={visible} transparent animationType="fade" onRequestClose={onClose}>
      <TouchableOpacity style={styles.overlay} activeOpacity={1} onPress={onClose}>
        <TouchableOpacity style={styles.modalContainer} activeOpacity={1} onPress={(e) => e.stopPropagation()}>
          <View style={styles.header}>
            <View>
              <Text style={styles.title}>Assign Calendar</Text>
              <Text style={styles.subtitle}>{calendarName}</Text>
            </View>
            <TouchableOpacity onPress={onClose} style={styles.closeButton}>
              <Ionicons name="close" size={24} color={colors.text.primary as string} />
            </TouchableOpacity>
          </View>

          <ScrollView style={styles.content}>
            <View style={styles.sectionHeader}>
              <View style={styles.sectionTitleRow}>
                <Ionicons name="person" size={16} color={colors.text.secondary as string} />
                <Text style={styles.sectionTitle}>Users</Text>
              </View>
            </View>

            {isLoadingUsers ? (
              <ActivityIndicator size="small" color={colors.primary as string} style={{ marginBottom: spacing.md }} />
            ) : (
              users.map((u) => (
                <TouchableOpacity
                  key={u._id}
                  style={[styles.itemContainer, isUserShared(u._id) && styles.itemContainerActive]}
                  onPress={() => toggleUser(u._id)}
                >
                  <Text style={styles.itemName}>{`${u.firstName || ''} ${u.lastName || ''}`.trim() || u.email}</Text>
                  {isUserShared(u._id) && <Ionicons name="checkmark-circle" size={22} color={colors.success as string} />}
                </TouchableOpacity>
              ))
            )}

            <View style={styles.sectionDivider} />

            <View style={styles.sectionHeader}>
              <View style={styles.sectionTitleRow}>
                <Ionicons name="people" size={16} color={colors.text.secondary as string} />
                <Text style={styles.sectionTitle}>Departments</Text>
              </View>
            </View>

            {isLoadingDepartments ? (
              <ActivityIndicator size="small" color={colors.primary as string} style={{ marginBottom: spacing.md }} />
            ) : (
              departments.map((d) => (
                <TouchableOpacity
                  key={d._id}
                  style={[styles.itemContainer, isDepartmentShared(d._id) && styles.itemContainerActive]}
                  onPress={() => toggleDepartment(d._id)}
                >
                  <Text style={styles.itemName}>{d.name}</Text>
                  {isDepartmentShared(d._id) && <Ionicons name="checkmark-circle" size={22} color={colors.success as string} />}
                </TouchableOpacity>
              ))
            )}

            <TouchableOpacity style={[styles.saveButton, saving && styles.saveButtonDisabled]} onPress={handleSave} disabled={saving} activeOpacity={0.85}>
              {saving ? <ActivityIndicator size="small" color="#FFFFFF" /> : <Text style={styles.saveButtonText}>Save</Text>}
            </TouchableOpacity>
          </ScrollView>
        </TouchableOpacity>
      </TouchableOpacity>
    </Modal>
  );
};

const getStyles = () => StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: Platform.OS === 'web' ? 'center' : 'flex-start',
    alignItems: Platform.OS === 'web' ? 'center' : 'stretch',
    padding: Platform.OS === 'web' ? spacing.lg : 0,
    paddingTop: Platform.OS === 'web' ? spacing.lg : 0,
    ...(Platform.OS !== 'web' && {
      position: 'absolute',
      top: MOBILE_HEADER_OFFSET,
      left: 0,
      right: 0,
      bottom: 0,
    }),
  },
  modalContainer: {
    backgroundColor: colors.background.surface,
    borderRadius: Platform.OS === 'web' ? 16 : 0,
    width: '100%',
    maxWidth: Platform.OS === 'web' ? 480 : undefined,
    maxHeight: Platform.OS === 'web' ? '85%' : undefined,
    flex: Platform.OS === 'web' ? undefined : 1,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.3,
    shadowRadius: 8,
    elevation: 8,
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    padding: spacing.lg,
    borderBottomWidth: 1,
    borderBottomColor: colors.border as string,
  },
  title: {
    fontSize: typography.fontSize.xl,
    fontWeight: 'bold' as const,
    color: colors.text.primary as string,
    marginBottom: 4,
  },
  subtitle: { fontSize: typography.fontSize.sm, color: colors.text.secondary as string },
  closeButton: { padding: spacing.xs },
  content: { padding: spacing.lg },
  sectionHeader: { marginBottom: spacing.sm },
  sectionTitleRow: { flexDirection: 'row', alignItems: 'center', gap: spacing.xs, marginBottom: 4 },
  sectionTitle: { fontSize: typography.fontSize.base, fontWeight: '600' as const, color: colors.text.primary as string },
  sectionDivider: { height: 1, backgroundColor: colors.border as string, marginVertical: spacing.lg },
  itemContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: spacing.md,
    backgroundColor: colors.background.secondary as string,
    borderRadius: 12,
    marginBottom: spacing.sm,
    borderWidth: 2,
    borderColor: 'transparent',
  },
  itemContainerActive: {
    borderColor: colors.success as string,
    backgroundColor: withAlpha(colors.success, 0.06),
  },
  itemName: { fontSize: typography.fontSize.base, color: colors.text.primary as string },
  saveButton: {
    backgroundColor: colors.primary as string,
    borderRadius: 14,
    paddingVertical: spacing.md,
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: spacing.sm,
    marginBottom: spacing.lg,
  },
  saveButtonDisabled: { opacity: 0.5 },
  saveButtonText: { color: '#FFFFFF', fontSize: typography.fontSize.base, fontWeight: 'bold' as const },
});
